// `HelpTip` (rev-7, D-425) — the "?" beside a heading or a field that holds
// the explanation a screen used to print as a paragraph. The screen keeps at
// most one short lede; everything longer lives here or in the customer guide.
//
// A plain disclosure, not a dialog: the button carries `aria-expanded` and
// names the panel in `aria-controls`, the panel is ordinary text in the
// reading order right after the button, and nothing behind it is blocked.
// Escape and a press outside close it, and Escape gives focus back to the
// button. The panel floats, so opening it moves nothing on the page (§5).
//
// i18n-free like the other primitives: the caller passes the button's
// accessible name, the text, and the optional guide link's words.

import { useEffect, useId, useRef, useState } from "react";
import type { ReactNode } from "react";

export interface HelpTipProps {
  /** The button's accessible name, e.g. "About off-cut discounts". */
  label: string;
  /** The help itself: a sentence or two, or a short list. */
  children: ReactNode;
  /** A page of the customer guide that says more. Shown only when given. */
  guide?: { href: string; label: string } | undefined;
  "data-testid"?: string;
}

export function HelpTip({ label, children, guide, ...rest }: HelpTipProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const root = useRef<HTMLSpanElement | null>(null);
  const button = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e: PointerEvent): void => {
      if (root.current !== null && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const testId = rest["data-testid"] ?? "help-tip";
  return (
    <span className="ds-helptip" ref={root}>
      <button
        type="button"
        ref={button}
        className="ds-helptip-btn"
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        data-testid={testId}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="6.2" />
          <path d="M6.3 6.2a1.8 1.8 0 1 1 2.5 1.7c-.5.2-.8.6-.8 1.1v.4M8 11.4v.1" />
        </svg>
      </button>
      {open ? (
        <span className="ds-helptip-panel" id={panelId} role="note" data-testid={`${testId}-panel`}>
          <span className="ds-helptip-body">{children}</span>
          {guide !== undefined ? (
            <a className="ds-helptip-guide" href={guide.href}>
              {guide.label}
            </a>
          ) : null}
        </span>
      ) : null}
    </span>
  );
}
